import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveChannel } from '../../hooks/useLiveChannel';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Nachrichten im Sales-Bereich — der Kanal zwischen Sales-Partner und Rafael.
 *
 * WARUM ES DIESES PANEL GIBT
 * Der Nachrichtenspeicher (server/routes/partner-messages.ts) kann seit jeher
 * beide Richtungen: ein Partner darf im eigenen Namen an 'admin' schreiben, der
 * Server erzwingt das sogar. Erreichbar war das aber nur ueber die Inbox im
 * Arbeitsbereich. Wer im Sales-Bereich blieb — also genau der Personenkreis,
 * fuer den dieser Bereich gebaut wurde —, hatte kein Postfach: Rafaels acht
 * Nachrichten vom Juli lagen ungelesen da, und zurueckschreiben ging nur per
 * Telefon. Genau das soll der Bereich abschaffen.
 *
 * Bewusst ein Verlauf und kein Formular: der Sinn ist die Absprache mit
 * Kontext ("worauf antwortest du?"), nicht das Absetzen einer Meldung. Fuer
 * strukturierte Meldungen mit Anhang und Status gibt es weiterhin das
 * Feedback-Panel.
 */

const API = '/api/partner';

interface PartnerMessage {
  id: string;
  from: string;
  to: string;
  type: 'announcement' | 'direct' | 'system';
  status?: 'draft' | 'sent';
  subject: string;
  body: string;
  createdAt: string;
  readAt: string | null;
}

/** Absender-Anzeige. 'admin' ist die Kennung, unter der Rafael hinausschreibt. */
function senderLabel(from: string, ownName: string, ownId: string): string {
  if (from === ownId) return ownName;
  if (from === 'admin' || from === 'rafael') return 'Rafael';
  return from;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const markdownComponents = {
  p: ({ ...props }) => <p style={{ margin: '0 0 8px' }} {...props} />,
  ul: ({ ...props }) => <ul style={{ margin: '0 0 8px', paddingLeft: 18 }} {...props} />,
  ol: ({ ...props }) => <ol style={{ margin: '0 0 8px', paddingLeft: 18 }} {...props} />,
  li: ({ ...props }) => <li style={{ marginBottom: 3 }} {...props} />,
  a: ({ ...props }) => <a style={{ color: 'var(--tn-blue)' }} target="_blank" rel="noreferrer" {...props} />,
  code: ({ ...props }) => (
    <code style={{ background: 'var(--tn-bg-dark)', padding: '1px 4px', borderRadius: 3, fontSize: 12 }} {...props} />
  ),
};

export default function SalesMessagesPanel() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const [messages, setMessages] = useState<PartnerMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  const loadMessages = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/messages?userId=${encodeURIComponent(userId)}`, {
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = (raw as { messages?: unknown[] }).messages;
      if (!Array.isArray(list)) throw new Error('API /api/partner/messages: erwartet { messages: [] }');
      const data = list.map(item =>
        validateApiResponse<PartnerMessage>(item, '/api/partner/messages[item]', {
          id: 'string',
          from: 'string',
          to: 'string',
          type: 'string',
          status: { type: 'string', optional: true },
          subject: { type: 'string', optional: true },
          body: 'string',
          createdAt: 'string',
          readAt: { type: 'string', optional: true },
        }),
      );
      // Aeltestes zuerst: ein Gespraech liest man von oben nach unten.
      data.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      setMessages(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useLiveChannel({
    label: 'SalesMessages',
    onResync: () => { loadMessages(); },
    onMessage: (msg: { type?: string }) => {
      // Jede Aenderung am Postfach neu laden statt einzelne Ereignisse
      // nachzubilden: der Verlauf ist kurz, und ein zweiter Nachbau der
      // Sichtbarkeitsregeln waere genau die Stelle, an der er auseinanderlaeuft.
      if (msg.type === 'partner-message-new' || msg.type === 'partner-message-approved'
          || msg.type === 'partner-message-deleted') {
        loadMessages();
      }
    },
  });

  // Ungelesene als gelesen melden, sobald sie im Verlauf stehen — sonst haengt
  // Rafaels Zaehler auf "ungelesen", obwohl der Partner sie vor sich hat.
  useEffect(() => {
    const unread = messages.filter(m => !m.readAt && m.from !== userId);
    if (unread.length === 0) return;
    const ids = unread.map(m => m.id);
    fetch(`${API}/messages/mark-read-bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
      signal: AbortSignal.timeout(15000),
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); })
      .then(() => setMessages(prev => prev.map(m => (ids.includes(m.id) ? { ...m, readAt: new Date().toISOString() } : m))))
      .catch(err => console.error('[SalesMessages] mark-read failed:', err));
  }, [messages, userId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: userId,
          to: 'admin',
          type: 'direct',
          // Der Server erzwingt fuer Partner from/to/type — die Werte hier sind
          // keine Wahl, sondern die einzig zulaessige Kombination.
          subject: 'Nachricht aus dem Sales-Bereich',
          body,
          status: 'sent',
        }),
        signal: AbortSignal.timeout(20000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setDraft('');
      await loadMessages();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }, [draft, userId, loadMessages]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-bg)' }}>
      <div style={{
        padding: '10px 16px', borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
        display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--tn-text)' }}>Nachrichten</span>
        <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
          Dein direkter Draht zu Rafael — alles steht hier, nichts geht per Telefon verloren.
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 16px 8px' }}>
        {loading && <div style={{ color: 'var(--tn-text-muted)', fontSize: 13 }}>Lädt…</div>}

        {error && (
          <div style={{
            padding: 12, borderRadius: 6, background: 'rgba(247,118,142,0.12)',
            border: '1px solid var(--tnf-f7768e)', color: 'var(--tnf-f7768e)', fontSize: 13, marginBottom: 12,
          }}>
            Nachrichten konnten nicht geladen werden: {error}
            <button
              data-ai-id="sales-messages-retry"
              onClick={() => { setLoading(true); loadMessages(); }}
              style={{
                marginLeft: 10, padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                background: 'var(--tn-surface)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
              }}
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {!loading && !error && messages.length === 0 && (
          <div style={{ color: 'var(--tn-text-muted)', fontSize: 13 }}>
            Noch keine Nachrichten. Schreib unten die erste.
          </div>
        )}

        {messages.map(m => {
          const mine = m.from === userId;
          return (
            <div
              key={m.id}
              data-ai-id={`sales-message-${m.id}`}
              style={{
                display: 'flex', flexDirection: 'column',
                alignItems: mine ? 'flex-end' : 'flex-start', marginBottom: 14,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 3 }}>
                {senderLabel(m.from, user?.name ?? 'Ich', userId)} · {formatWhen(m.createdAt)}
                {m.type === 'announcement' && ' · Ankündigung an alle Partner'}
              </div>
              <div style={{
                maxWidth: '78%', padding: '10px 13px', borderRadius: 10, fontSize: 13, lineHeight: 1.55,
                background: mine ? 'var(--tn-blue)' : 'var(--tn-surface)',
                color: mine ? '#fff' : 'var(--tn-text)',
                border: mine ? 'none' : '1px solid var(--tn-border)',
                overflowWrap: 'anywhere',
              }}>
                {m.subject && m.subject !== 'Nachricht aus dem Sales-Bereich' && (
                  <div style={{ fontWeight: 700, marginBottom: 5 }}>{m.subject}</div>
                )}
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {m.body}
                </ReactMarkdown>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div style={{ borderTop: '1px solid var(--tn-border)', padding: 12, flexShrink: 0 }}>
        {sendError && (
          <div style={{ color: 'var(--tnf-f7768e)', fontSize: 12, marginBottom: 6 }}>
            Nicht gesendet: {sendError}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            data-ai-id="sales-messages-input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); }
            }}
            placeholder="Nachricht an Rafael… (Strg+Enter sendet)"
            rows={3}
            style={{
              flex: 1, resize: 'vertical', padding: '9px 11px', borderRadius: 6, fontSize: 13,
              fontFamily: 'inherit', background: 'var(--tn-surface)', color: 'var(--tn-text)',
              border: '1px solid var(--tn-border)',
            }}
          />
          <button
            data-ai-id="sales-messages-send"
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            style={{
              padding: '10px 20px', borderRadius: 6, fontSize: 13, fontWeight: 700,
              cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
              background: sending || !draft.trim() ? 'var(--tn-surface)' : 'var(--tn-blue)',
              color: sending || !draft.trim() ? 'var(--tn-text-muted)' : '#fff',
              border: '1px solid var(--tn-border)',
            }}
          >
            {sending ? 'Sendet…' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
